// --- Dependencies -------------------------------------------------------------------------------
import { MenuItem, Select, Switch, TextField } from "@mui/material";
import { useEffect, useRef, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import { getCellFieldSx, getCellMenuProps } from "@/utils/inputSx";
import type { Props } from "./types";
import "./styles.css";

// --- Logic --------------------------------------------------------------------------------------

/** What goes into an input, whatever the model actually stores. */
function toInputValue(value: string | number | boolean | null, kind: string): string {
    if (value === null || value === undefined) return "";
    if (kind === "date") {
        const date = new Date(value as string);
        // A native date input only ever speaks `yyyy-mm-dd`.
        return isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
    }
    return String(value);
}

/**
 * One table cell that can be edited where it sits.
 *
 * The rule that matters is about trust: the cell shows a new value only once the server has accepted
 * it, and puts the old one back if it has not. An optimistic cell would let a refused edit sit on
 * screen looking saved, which on a dashboard whose whole job is to be believed is worse than being slow.
 *
 * A `select` reads as a badge until it is clicked and then opens its menu immediately — one click to
 * change a value rather than one to focus and another to open. Escape cancels, Enter commits, and
 * clicking away commits too: a half-typed value abandoned when the admin looks at another row is the
 * most common way an inline editor loses work.
 */
export default function EditableCell({ value, display, config, onSave, disabled }: Props) {
    const lang = useLang((state) => state.lang);
    const dir = lang === "en" ? "ltr" : "rtl";

    const [isEditing, setIsEditing] = useState(false);
    const [draft, setDraft] = useState(() => toInputValue(value, config.kind));
    const [isSaving, setIsSaving] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    /** A refetch can replace the row under an open editor; the draft follows unless it is being typed. */
    useEffect(() => {
        if (!isEditing) setDraft(toInputValue(value, config.kind));
    }, [value, config.kind, isEditing]);

    useEffect(() => {
        if (isEditing && config.kind !== "select") inputRef.current?.focus();
    }, [isEditing, config.kind]);

    const commit = async (raw: string | boolean) => {
        setIsEditing(false);

        let next: string | number | boolean | null = raw as string;
        if (config.kind === "toggle") next = Boolean(raw);
        else if (raw === "" && config.nullable) next = null;
        else if (config.kind === "number") {
            const parsed = Number(raw);
            if (isNaN(parsed)) return setDraft(toInputValue(value, config.kind));
            next = parsed;
        }

        if (next === value) return;

        setIsSaving(true);
        const ok = await onSave(next);
        setIsSaving(false);
        // The row's own value is the source of truth — a refused save simply never changes it.
        if (!ok) setDraft(toInputValue(value, config.kind));
    };

    if (disabled) return <span className="editable-cell editable-cell--locked">{display ?? toInputValue(value, config.kind) ?? "—"}</span>;

    // --- Toggle ---------------------------------------------------------------------------------

    if (config.kind === "toggle")
        return (
            <Switch
                size="small"
                checked={Boolean(value)}
                disabled={isSaving}
                onChange={(event) => commit(event.target.checked)}
                sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "var(--color-white)" },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "var(--color-success)", opacity: 1 },
                    "& .MuiSwitch-track": { backgroundColor: "var(--color-gray-01)", opacity: 1 },
                }}
            />
        );

    // --- Select ---------------------------------------------------------------------------------

    if (config.kind === "select") {
        if (!isEditing)
            return (
                <button type="button" className="editable-cell-badge" data-saving={isSaving} onClick={() => setIsEditing(true)}>
                    <span className="editable-cell-badge-text">{display ?? (toInputValue(value, config.kind) || "—")}</span>
                    <img className="editable-cell-badge-caret" src="/icons/chevron-down-icon.svg" alt="" width={10} height={10} />
                </button>
            );

        return (
            <Select
                open
                size="small"
                value={draft}
                sx={getCellFieldSx(dir)}
                MenuProps={getCellMenuProps(dir)}
                onClose={() => setIsEditing(false)}
                onChange={(event) => commit(event.target.value as string)}
            >
                {config.nullable && <MenuItem value="">—</MenuItem>}
                {(config.options ?? []).map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                        {option.label}
                    </MenuItem>
                ))}
            </Select>
        );
    }

    // --- Text, number, date ---------------------------------------------------------------------

    if (!isEditing)
        return (
            <button type="button" className="editable-cell" data-saving={isSaving} onClick={() => setIsEditing(true)}>
                <span className="editable-cell-text">{display ?? (toInputValue(value, config.kind) || "—")}</span>
                <img className="editable-cell-pencil" src="/icons/edit-icon.svg" alt="" width={11} height={11} />
            </button>
        );

    return (
        <TextField
            inputRef={inputRef}
            size="small"
            type={config.kind === "number" ? "number" : config.kind === "date" ? "date" : "text"}
            value={draft}
            placeholder={config.placeholder}
            sx={getCellFieldSx(dir)}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={() => commit(draft)}
            onKeyDown={(event) => {
                if (event.key === "Enter") commit(draft);
                if (event.key === "Escape") {
                    setDraft(toInputValue(value, config.kind));
                    setIsEditing(false);
                }
            }}
        />
    );
}
